import {
  backendGet,
  requestTokenRefresh,
  requestLogout,
} from "./dataService.js";

const storeAuthToken = (token) => {
  sessionStorage.setItem("authData", JSON.stringify(token));
};

// Cache for the refresh token promise to prevent multiple concurrent refreshes
let refreshTokenPromise = null;

export const getAuthToken = async (forceRefresh = false) => {
  if (forceRefresh) {
    return refreshToken();
  }

  const authData = sessionStorage.getItem("authData");
  let access = null;
  try {
    access = authData ? JSON.parse(authData) : null;
  } catch (e) {
    console.error("Error parsing authData", e);
  }
  if (!access || tokenNeedsRefresh(access)) {
    return refreshToken();
  }

  return access.token;
};

const tokenNeedsRefresh = (access) => {
  const token_expires_timestamp = new Date(access.expires_at).getTime();
  const now = new Date().getTime();
  // Check if the token is about to expire (with a buffer time)
  const buffer_time = 30 * 1000; // 30 seconds buffer

  return Number(token_expires_timestamp) - buffer_time < Number(now);
};

const refreshToken = async () => {
  // If a refresh is already in progress, return the existing promise
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  // Create a new refresh token promise
  refreshTokenPromise = (async () => {
    try {
      const res = await requestTokenRefresh();
      if (
        res.status === 200 &&
        res.data &&
        res.data.data &&
        res.data.data.access_token
      ) {
        const access = res.data.data.access_token;
        storeAuthToken(access);
        await fetchUser();
        return access.token;
      } else {
        redirectToLogin();
        return null;
      }
    } catch (e) {
      redirectToLogin();
      return null;
    } finally {
      // Reset the refresh state and promise when done
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
};

export const fetchUser = async () => {
  try {
    let userData = null;
    await backendGet("auth/me", {
      setResult: (data) => {
        setUser(data);
        userData = data;
      },
    });
    return userData;
  } catch (userError) {
    console.error("Failed to fetch user data after token refresh", userError);
    return null;
  }
};

// Process a successful login API response
export const processLoginSuccess = async (data) => {
  if (data && data.access_token) {
    storeAuthToken(data.access_token);
    return await fetchUser();
  }
  console.error(
    "processLoginSuccess: Invalid login API data or missing access token"
  );
  return null;
};

/** Used for global state management of the user data */
let globalUserUpdateCallback = null;

/**
 * Registers a global callback function to be invoked whenever the user data is set.
 * This is primarily used by UserContextProvider to keep its state in sync.
 * @param {Function} callback - The function to call with the new user data.
 */
export const registerGlobalUserUpdateCallback = (callback) => {
  globalUserUpdateCallback = callback;
};

const setUser = (user, callback = null) => {
  sessionStorage.setItem("user", JSON.stringify(user));
  // If a per-call callback is provided, call it with the user data
  if (callback && typeof callback === "function") {
    callback(user);
  }
  // Also call the globally registered callback, if any
  if (
    globalUserUpdateCallback &&
    typeof globalUserUpdateCallback === "function"
  ) {
    globalUserUpdateCallback(user);
  }
};

export const getUser = async (try_refresh = true) => {
  const user = JSON.parse(sessionStorage.getItem("user"));

  if (user) {
    return user;
  }

  if (try_refresh) {
    await refreshToken();
    return getUser(false);
  }

  return null;
};

const clearAuthData = () => {
  sessionStorage.removeItem("authData");
  sessionStorage.removeItem("user");
  // Logout also drops the Kundenportal's customer selection.
  sessionStorage.removeItem("customer_selection");
  // Ensure UserContext is updated when auth data is cleared
  setUser(null);
};

export const saveRedirectUrl = () => {
  const current_path = window.location.pathname;
  const current_search = window.location.search;

  // Only store the path if it's not the root or login page
  if (current_path !== "/" && current_path !== "/login") {
    const redirect_url = `${current_path}${current_search}`;
    sessionStorage.setItem("redirect_after_login", redirect_url);
  }
};

/**
 * Reads and clears the redirect URL stored in session storage.
 * This function is idempotent and returns null if no redirect URL is stored.
 * @returns {string|null} The redirect URL, or null if none is stored.
 */
export const readRedirectUrlOnce = () => {
  const redirect_url = sessionStorage.getItem("redirect_after_login");
  if (redirect_url) {
    sessionStorage.removeItem("redirect_after_login");
  }

  return redirect_url;
};

export const redirectToLogin = () => {
  clearAuthData();
  saveRedirectUrl();

  // Check if we're already on the login page to prevent redirect loops
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

/**
 * Logs out the user by clearing the auth data and redirecting to the login page.
 * If the API call fails, it will not clear the auth data. Thus, callers are expected
 * to inform the user that logout failed.
 *
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    await requestLogout();
    // Clear local auth data only if API call is successful
    clearAuthData();
  } catch (e) {
    console.error("Logout failed:", e);
    throw e;
  }
};
